export interface AgentSkillDefinition {
  id: string;
  name: string;
  description: string;
  instructions: string;
  integrationScopes?: string[];
  outputSchema?: Record<string, unknown>;
  isBuiltIn: boolean;
}

export const BUILTIN_AGENT_SKILLS: AgentSkillDefinition[] = [
  {
    id: "skill-meeting-separate-chat",
    name: "Separate Business vs Casual Chat",
    description: "Split meeting transcript into business substance and normal casual conversation.",
    isBuiltIn: true,
    integrationScopes: ["fireflies", "zoom"],
    instructions: `Analyze the meeting transcript and separate content into two distinct sections:

**businessContent** — decisions, commitments, project updates, metrics, deadlines, risks, stakeholders, actionable topics.

**casualChat** — greetings, small talk, off-topic banter, social rapport, jokes, unrelated personal chat.

Include a confidence score (0-1) for how clearly the separation could be made.`,
    outputSchema: {
      type: "object",
      properties: {
        businessContent: {
          type: "object",
          properties: {
            summary: { type: "string" },
            decisions: { type: "array", items: { type: "string" } },
            commitments: { type: "array", items: { type: "string" } },
            openQuestions: { type: "array", items: { type: "string" } },
          },
        },
        casualChat: {
          type: "object",
          properties: {
            summary: { type: "string" },
            notableSocialContext: { type: "string" },
          },
        },
        confidence: { type: "number" },
        quality: { type: "number" },
      },
    },
  },
  {
    id: "skill-meeting-business-extract",
    name: "Business Information Extractor",
    description: "Extract business information from meetings: decisions, revenue, projects, risks.",
    isBuiltIn: true,
    integrationScopes: ["fireflies", "zoom"],
    instructions: `Extract all business-relevant information from the content. Focus on decisions, financial mentions, project status, risks, stakeholders, and strategic direction. Ignore casual conversation.`,
    outputSchema: {
      type: "object",
      properties: {
        summary: { type: "string" },
        decisions: { type: "array", items: { type: "string" } },
        projects: { type: "array", items: { type: "string" } },
        risks: { type: "array", items: { type: "string" } },
        confidence: { type: "number" },
        quality: { type: "number" },
      },
    },
  },
  {
    id: "skill-meeting-deliverables",
    name: "Deliverable Architect",
    description: "Extract commitments: what needs to be done, who agreed, when due.",
    isBuiltIn: true,
    integrationScopes: ["fireflies", "zoom", "gmail"],
    instructions: `Identify concrete deliverables and action items from the meeting. For each deliverable specify: title, description, owner (if mentioned), due date (if mentioned), priority.

Also detect which ClickUp project/list this meeting relates to based on project names, client names, or team discussion context. Return detectedProject with name, optional listId if clearly identified, confidence (0-1), and reason.

Return relatedExistingTasks as an empty array (filled later from ClickUp API).`,
    outputSchema: {
      type: "object",
      properties: {
        detectedProject: {
          type: "object",
          properties: {
            name: { type: "string" },
            listId: { type: "string" },
            confidence: { type: "number" },
            reason: { type: "string" },
          },
        },
        deliverables: {
          type: "array",
          items: {
            type: "object",
            properties: {
              title: { type: "string" },
              description: { type: "string" },
              owner: { type: "string" },
              dueDate: { type: "string" },
              priority: { type: "string" },
            },
          },
        },
        relatedExistingTasks: {
          type: "array",
          items: {
            type: "object",
            properties: {
              id: { type: "string" },
              name: { type: "string" },
              relevance: { type: "number" },
              reason: { type: "string" },
            },
          },
        },
        confidence: { type: "number" },
        quality: { type: "number" },
      },
    },
  },
  {
    id: "skill-meeting-participants",
    name: "Participant Mapper",
    description: "Map who said what and identify follow-up owners.",
    isBuiltIn: true,
    integrationScopes: ["fireflies", "zoom"],
    instructions: `List participants, their roles if mentioned, key contributions, and suggested follow-up owners.`,
  },
  {
    id: "skill-meeting-executive-summary",
    name: "Executive Summary (BLUF)",
    description: "Bottom-line-up-front summary for leadership.",
    isBuiltIn: true,
    integrationScopes: ["fireflies", "zoom", "gmail"],
    instructions: `Write a concise BLUF executive summary in 3-5 bullet points. Lead with the most important outcome.`,
  },
  {
    id: "skill-gmail-spam-classify",
    name: "Spam Classifier",
    description: "Classify email as spam or legitimate with confidence.",
    isBuiltIn: true,
    integrationScopes: ["gmail"],
    instructions: `Classify whether this email is unwanted spam.

NOT spam (default when uncertain): support or help-desk replies, billing and account notifications, transactional mail from vendors the user likely signed up for (e.g. Anytime Mailbox, SaaS providers), ticket updates, shipping receipts, security alerts for known services, and personal correspondence.

Spam (only with high confidence): unsolicited marketing, phishing, obvious scams, bulk promotions with no prior relationship, and messages with deceptive sender domains or urgent credential requests.

Rules:
- Set spam: true ONLY if confidence is at least 0.85. If uncertain, set spam: false and explain why in reason.
- reason must cite specific signals (e.g. "support ticket reply from known vendor" vs "unsolicited promo link").
- Do not flag legitimate business or service mail as spam because of promotional footers or automated formatting.

Return spam (boolean), confidence (0-1), and reason (string).`,
    outputSchema: {
      type: "object",
      properties: {
        spam: { type: "boolean" },
        confidence: { type: "number" },
        reason: { type: "string" },
      },
    },
  },
  {
    id: "skill-gmail-reply-draft",
    name: "Gmail Reply Draft",
    description: "Draft a professional email reply with subject and body.",
    isBuiltIn: true,
    integrationScopes: ["gmail"],
    instructions: `Draft a professional reply to the email. Return subject, body, tone, and confidence.`,
    outputSchema: {
      type: "object",
      properties: {
        subject: { type: "string" },
        body: { type: "string" },
        tone: { type: "string" },
        confidence: { type: "number" },
      },
    },
  },
  {
    id: "skill-gmail-importance",
    name: "Importance Notifier",
    description: "Identify important emails and upcoming events.",
    isBuiltIn: true,
    integrationScopes: ["gmail"],
    instructions: `Assess importance. Return important (boolean), summary, and events array with dates.`,
  },
  {
    id: "skill-schema-json",
    name: "Structured JSON Output",
    description: "Enforce strict JSON output matching the configured schema.",
    isBuiltIn: true,
    instructions: `You MUST respond with valid JSON only, matching the output schema provided. No markdown fences.`,
  },
];

export function getSkillById(id: string): AgentSkillDefinition | undefined {
  return BUILTIN_AGENT_SKILLS.find((s) => s.id === id);
}

export function getSkillsForScopes(scopes: string[]): AgentSkillDefinition[] {
  if (!scopes.length) return BUILTIN_AGENT_SKILLS;
  return BUILTIN_AGENT_SKILLS.filter(
    (s) => !s.integrationScopes?.length || s.integrationScopes.some((sc) => scopes.includes(sc))
  );
}

export type BuiltinSkillFormValues = {
  name: string;
  description: string;
  instructions: string;
  integrationScopes: string[];
  outputSchema?: Record<string, unknown>;
};

export function builtinToFormValues(skill: AgentSkillDefinition): BuiltinSkillFormValues {
  return {
    name: `Copy of ${skill.name}`,
    description: skill.description,
    instructions: skill.instructions,
    integrationScopes: skill.integrationScopes ?? [],
    outputSchema:
      skill.outputSchema && typeof skill.outputSchema === "object"
        ? (skill.outputSchema as Record<string, unknown>)
        : undefined,
  };
}

export function composeSkillInstructions(skillIds: string[]): string {
  const blocks: string[] = [];
  for (const id of skillIds) {
    const skill = getSkillById(id);
    if (skill) {
      blocks.push(`## SKILL: ${skill.name}\n${skill.instructions}`);
    }
  }
  return blocks.join("\n\n");
}
